/**
 * LogisticsTab — latest_delivery, delivery_term/place, logistic_cost, trader.
 */
import { Box, MenuItem, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import {
  useListDeliveryPlacesQuery,
  useListDeliveryTermsQuery,
  useListTradersQuery,
} from '@/store/api/lookupsApi'
import type { LocalPFormValues } from './types'

export interface LogisticsTabProps {
  control: Control<LocalPFormValues>
  errors: FieldErrors<LocalPFormValues>
}

export function LogisticsTab({ control, errors }: LogisticsTabProps) {
  const { data: places = [] } = useListDeliveryPlacesQuery()
  const { data: terms = [] } = useListDeliveryTermsQuery()
  const { data: traders = [] } = useListTradersQuery()

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="latest_delivery"
          render={({ field }) => (
            <DatePicker
              label="Latest Delivery"
              format="DD-MMM-YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: '1 1 220px' },
                  error: !!errors.latest_delivery,
                  helperText: errors.latest_delivery?.message,
                },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="delivery_term"
          render={({ field }) => (
            <TextField
              {...field}
              label="Delivery Term"
              select
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.delivery_term}
              helperText={errors.delivery_term?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {terms.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="delivery_place_code"
          render={({ field }) => (
            <TextField
              {...field}
              label="Delivery Place"
              select
              size="small"
              sx={{ flex: '1 1 220px' }}
              error={!!errors.delivery_place_code}
              helperText={errors.delivery_place_code?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {places.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="logistic_cost"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Logistic Cost"
              type="number"
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.logistic_cost}
              helperText={errors.logistic_cost?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="trader_in_charge"
          render={({ field }) => (
            <TextField
              {...field}
              label="Trader in Charge"
              select
              size="small"
              sx={{ flex: '1 1 240px' }}
              error={!!errors.trader_in_charge}
              helperText={errors.trader_in_charge?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {traders.map((t) => (
                <MenuItem key={t.username} value={t.username}>
                  {t.full_name} ({t.username})
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>
    </Box>
  )
}

export default LogisticsTab
